import {
  Button,
  Checkbox,
  ClickableTableRow,
  ConfirmDialog,
  EditButton,
  IconButton,
  OpenLink,
  RetryButton,
  StopIcon,
  TrashButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import {
  hideRunInputTypeColumn,
  hideRunVersionColumn,
  showRunStartDateColumn,
  showRunTimeoutColumn,
} from "../../lib/featureFlags";
import { when } from "../../lib/time";
import { formatDurationSeconds } from "../../lib/usageAggregation";
import {
  agentRunRetryRequest,
  DEFAULT_AGENT_START_DATE,
  useCancelAgentRun,
  useCancelAgentRuns,
  useCreateAgentRun,
  useDeleteAgentRun,
  useDeleteAgentRuns,
  useUpdateAgentRunsVisibility,
  type AgentRun,
  type AgentVisibility,
} from "../api/e2e";
import { formatCountry } from "../lib/companyCatalog";
import { duration } from "../lib/format";
import {
  canonicalRunDestination,
  companyRunPath,
  publishedModelPath,
} from "../lib/runRoutes";
import AgentStatusPill from "./AgentStatusPill";
import DraftRunEditorModal from "./DraftRunEditorModal";
import RunTrackIndicator from "./RunTrackIndicator";
import { AgentInputModeBadge } from "./AgentRunProfileBadges";
import RunHypothesisHoverCard from "./RunHypothesisHoverCard";
import RunLineageHoverCard from "./RunLineageHoverCard";
import RunVisibilityHoverCard from "./RunVisibilityHoverCard";

import "./AgentRunTable.css";

function PublishedModelAction({ run }: { run: AgentRun }) {
  const path = publishedModelPath(run);
  if (!path) return null;
  return <OpenLink to={path} label={`Open ${run.ticker} model`} />;
}

function runCount(count: number): string {
  return `${count} ${count === 1 ? "run" : "runs"}`;
}

function userStartedRun(run: AgentRun, subject: string | undefined): boolean {
  return subject !== undefined && run.started_by?.subject === subject;
}

export default function AgentRunTable({
  runs,
  showVersion,
  showModelColumns = true,
  showHypothesis = true,
}: {
  runs: AgentRun[];
  showVersion: boolean;
  showModelColumns?: boolean;
  showHypothesis?: boolean;
}) {
  const auth = useAuth();
  const navigate = useNavigate();
  const inputTypeColumnVisible = !hideRunInputTypeColumn();
  const versionColumnVisible = showVersion && !hideRunVersionColumn();
  const timeoutColumnVisible = showRunTimeoutColumn();
  const startDateColumnVisible = showRunStartDateColumn();
  const [editingDraftId, setEditingDraftId] = useState<string | null>(null);
  const [pendingCancel, setPendingCancel] = useState<AgentRun | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AgentRun | null>(null);
  const [pendingBulkCancel, setPendingBulkCancel] = useState(false);
  const [pendingBulkDelete, setPendingBulkDelete] = useState(false);
  const [pendingRetry, setPendingRetry] = useState<AgentRun | null>(null);
  const [selectedRunIds, setSelectedRunIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const cancelRun = useCancelAgentRun();
  const deleteRun = useDeleteAgentRun();
  const deleteRuns = useDeleteAgentRuns();
  const cancelRuns = useCancelAgentRuns();
  const updateRunsVisibility = useUpdateAgentRunsVisibility();
  const retryRun = useCreateAgentRun();
  const manageableRuns = useMemo(
    () =>
      runs.filter(
        (run) => auth.canOperate || userStartedRun(run, auth.identity?.subject),
      ),
    [auth.canOperate, auth.identity?.subject, runs],
  );
  const manageableRunIds = useMemo(
    () => new Set(manageableRuns.map((run) => run.id)),
    [manageableRuns],
  );
  const ownedRunIds = useMemo(
    () =>
      new Set(
        runs
          .filter((run) => userStartedRun(run, auth.identity?.subject))
          .map((run) => run.id),
      ),
    [auth.identity?.subject, runs],
  );
  const selectedRuns = manageableRuns.filter((run) =>
    selectedRunIds.has(run.id),
  );
  const selectedOwnedRuns = selectedRuns.filter(
    (run) => auth.canOperate || ownedRunIds.has(run.id),
  );
  const cancellableRuns = selectedRuns.filter(
    (run) => run.state.kind !== "terminal",
  );
  const privateRuns = selectedOwnedRuns.filter(
    (run) => run.visibility === "private",
  );
  const publicRuns = selectedOwnedRuns.filter(
    (run) => run.visibility === "public",
  );
  const bulkActionPending =
    cancelRuns.isPending ||
    updateRunsVisibility.isPending ||
    deleteRun.isPending ||
    deleteRuns.isPending;
  const allManageableSelected =
    manageableRuns.length > 0 &&
    manageableRuns.every((run) => selectedRunIds.has(run.id));

  useEffect(() => {
    setSelectedRunIds((current) => {
      const retained = new Set(
        [...current].filter((runId) => manageableRunIds.has(runId)),
      );
      return retained.size === current.size ? current : retained;
    });
  }, [manageableRunIds]);

  const cancel = (run: AgentRun) => {
    cancelRun.mutate(run.id, {
      onSuccess: () => {
        toast.success("Cancellation requested.");
        setPendingCancel(null);
      },
      onError: (error) => toast.error(error.message),
    });
  };

  const retry = (run: AgentRun) => {
    setPendingRetry(null);
    retryRun.mutate(agentRunRetryRequest(run), {
      onSuccess: (retriedRun) => {
        toast.success(`${retriedRun.ticker} run retried.`);
        navigate(companyRunPath(retriedRun));
      },
      onError: (error) => toast.error(error.message),
    });
  };

  const updateVisibility = (
    visibility: AgentVisibility,
    targets: readonly AgentRun[],
  ) => {
    updateRunsVisibility.mutate(
      { runIds: targets.map((run) => run.id), visibility },
      {
        onSuccess: () => {
          toast.success(
            `${runCount(targets.length)} ${targets.length === 1 ? "is" : "are"} now ${visibility}.`,
          );
          setSelectedRunIds(new Set());
        },
        onError: (error) => toast.error(error.message),
      },
    );
  };

  const cancelSelectedRuns = () => {
    cancelRuns.mutate(
      cancellableRuns.map((run) => run.id),
      {
        onSuccess: () => {
          toast.success(
            `Cancellation requested for ${runCount(cancellableRuns.length)}.`,
          );
          setPendingBulkCancel(false);
          setSelectedRunIds(new Set());
        },
        onError: (error) => toast.error(error.message),
      },
    );
  };

  const deleteSelectedRuns = () => {
    deleteRuns.mutate(
      selectedRuns.map((run) => run.id),
      {
        onSuccess: (results) => {
          const failed = results.filter((result) => result.error !== null);
          const deletedCount = results.length - failed.length;
          if (deletedCount > 0) {
            toast.success(`${runCount(deletedCount)} deleted.`);
          }
          if (failed.length > 0) {
            toast.error(
              `${runCount(failed.length)} could not be deleted. ${failed[0]?.error ?? ""}`,
            );
          }
          setSelectedRunIds(new Set(failed.map((result) => result.runId)));
          setPendingBulkDelete(false);
        },
        onError: (error) => toast.error(error.message),
      },
    );
  };

  return (
    <div className="agent-run-table-wrap">
      {selectedRuns.length > 0 && (
        <div
          className="agent-run-bulk-actions"
          role="toolbar"
          aria-label="Selected run actions"
        >
          <strong>{runCount(selectedRuns.length)} selected</strong>
          {selectedOwnedRuns.length > 0 && (
            <>
              <Button
                disabled={bulkActionPending || privateRuns.length === 0}
                onClick={() => updateVisibility("public", privateRuns)}
              >
                Make public ({privateRuns.length})
              </Button>
              <Button
                disabled={bulkActionPending || publicRuns.length === 0}
                onClick={() => updateVisibility("private", publicRuns)}
              >
                Make private ({publicRuns.length})
              </Button>
            </>
          )}
          <Button
            tone="secondary"
            iconLeft={<StopIcon />}
            disabled={bulkActionPending || cancellableRuns.length === 0}
            onClick={() => setPendingBulkCancel(true)}
          >
            Cancel ({cancellableRuns.length})
          </Button>
          <Button
            tone="danger"
            disabled={bulkActionPending}
            onClick={() => setPendingBulkDelete(true)}
          >
            Delete ({selectedRuns.length})
          </Button>
          <Button
            tone="quiet"
            disabled={bulkActionPending}
            onClick={() => setSelectedRunIds(new Set())}
          >
            Clear selection
          </Button>
        </div>
      )}
      <div className="agent-run-table-scroll">
        <Table className="agent-run-table">
          <TableHead>
            <TableRow>
              <TableHeader className="agent-run-table__selection">
                <Checkbox
                  checked={allManageableSelected}
                  disabled={bulkActionPending || manageableRuns.length === 0}
                  label={<span className="sr-only">Select all runs</span>}
                  onCheckedChange={(checked) =>
                    setSelectedRunIds(
                      checked
                        ? new Set(manageableRuns.map((run) => run.id))
                        : new Set(),
                    )
                  }
                />
              </TableHeader>
              <TableHeader className="agent-run-table__company">
                Company
              </TableHeader>
              {inputTypeColumnVisible && (
                <TableHeader className="agent-run-table__type">
                  Type
                </TableHeader>
              )}
              {showModelColumns && (
                <>
                  <TableHeader className="agent-run-table__model">
                    Model
                  </TableHeader>
                  <TableHeader className="agent-run-table__thinking">
                    Thinking
                  </TableHeader>
                </>
              )}
              {timeoutColumnVisible && (
                <TableHeader className="agent-run-table__timeout">
                  Timeout
                </TableHeader>
              )}
              {startDateColumnVisible && (
                <TableHeader className="agent-run-table__start-date">
                  Start date
                </TableHeader>
              )}
              {versionColumnVisible && (
                <TableHeader className="agent-run-table__version">
                  Version
                </TableHeader>
              )}
              <TableHeader className="agent-run-table__status">
                Status
              </TableHeader>
              <TableHeader className="agent-run-table__date">
                Created
              </TableHeader>
              <TableHeader className="agent-run-table__duration">
                Duration
              </TableHeader>
              <TableHeader
                className="agent-run-table__actions"
                aria-label="Actions"
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {runs.map((run) => {
              const selected = selectedRunIds.has(run.id);
              return (
                <ClickableTableRow
                  key={run.id}
                  aria-label={`Open ${run.ticker} run`}
                  aria-selected={selected}
                  className={selected ? "is-selected" : undefined}
                  href={canonicalRunDestination(run)}
                  onActivate={() => navigate(canonicalRunDestination(run))}
                >
                  <TableCell className="agent-run-table__selection">
                    <Checkbox
                      checked={selected}
                      disabled={
                        bulkActionPending || !manageableRunIds.has(run.id)
                      }
                      label={
                        <span className="sr-only">Select {run.ticker} run</span>
                      }
                      onCheckedChange={(checked) =>
                        setSelectedRunIds((current) => {
                          const next = new Set(current);
                          if (checked) next.add(run.id);
                          else next.delete(run.id);
                          return next;
                        })
                      }
                    />
                  </TableCell>
                  <TableCell className="agent-run-table__company">
                    <div className="agent-run-table__company-heading">
                      <strong>{run.ticker}</strong>
                      <RunVisibilityHoverCard
                        visibility={run.visibility}
                        startedBy={run.started_by}
                      />
                      {showHypothesis && (
                        <RunHypothesisHoverCard
                          ticker={run.ticker}
                          hypothesis={run.hypothesis}
                        />
                      )}
                      {run.base_model && (
                        <RunLineageHoverCard
                          ticker={run.ticker}
                          companyId={run.company_id}
                          baseModel={run.base_model}
                          iterationMessage={run.iteration_message}
                        />
                      )}
                    </div>
                    <span
                      className="agent-run-table__company-context"
                      title={`${run.company_name} · ${formatCountry(run.country)}`}
                    >
                      {run.company_name} · {formatCountry(run.country)}
                    </span>
                  </TableCell>
                  {inputTypeColumnVisible && (
                    <TableCell>
                      <AgentInputModeBadge mode={run.parameters.input_mode} />
                    </TableCell>
                  )}
                  {showModelColumns && (
                    <>
                      <TableCell className="agent-run-table__model">
                        <span className="mono" title={run.parameters.model}>
                          {run.parameters.model}
                        </span>
                      </TableCell>
                      <TableCell className="agent-run-table__thinking">
                        <span
                          className="mono"
                          title={run.parameters.reasoning_effort ?? undefined}
                        >
                          {run.parameters.reasoning_effort ?? "—"}
                        </span>
                      </TableCell>
                    </>
                  )}
                  {timeoutColumnVisible && (
                    <TableCell
                      className="agent-run-table__timeout"
                      title={`${run.parameters.timeout_seconds.toLocaleString()} seconds`}
                    >
                      {formatDurationSeconds(run.parameters.timeout_seconds)}
                    </TableCell>
                  )}
                  {startDateColumnVisible && (
                    <TableCell className="agent-run-table__start-date">
                      <span
                        className="mono"
                        title={
                          run.parameters.start_date ?? DEFAULT_AGENT_START_DATE
                        }
                      >
                        {run.parameters.start_date ?? DEFAULT_AGENT_START_DATE}
                      </span>
                    </TableCell>
                  )}
                  {versionColumnVisible && (
                    <TableCell className="agent-run-table__version">
                      <span
                        className="mono"
                        title={run.agent_version?.version ?? "Legacy"}
                      >
                        {run.agent_version?.version ?? "Legacy"}
                      </span>
                    </TableCell>
                  )}
                  <TableCell>
                    <div className="agent-run-status-actions">
                      <AgentStatusPill state={run.state} />
                      <RunTrackIndicator mode={run.parameters.run_mode} />
                      {run.state.kind === "draft" &&
                        ownedRunIds.has(run.id) && (
                          <EditButton
                            label={`Edit ${run.ticker} draft`}
                            onClick={() => setEditingDraftId(run.id)}
                          />
                        )}
                    </div>
                  </TableCell>
                  <TableCell className="agent-run-table__date">
                    <time dateTime={run.created_at}>
                      {when(run.created_at)}
                    </time>
                  </TableCell>
                  <TableCell className="agent-run-table__duration">
                    {duration(run.started_at, run.finished_at)}
                  </TableCell>
                  <TableCell className="agent-run-table__actions">
                    <div className="agent-run-row-actions">
                      <PublishedModelAction run={run} />
                      {run.state.kind === "terminal" &&
                        run.state.outcome === "failed" &&
                        (run.parameters.input_mode === "research" ||
                          run.inputs.some(
                            (input) => input.role === "canalyst",
                          )) && (
                          <RetryButton
                            label={`Retry ${run.ticker} run`}
                            disabled={retryRun.isPending}
                            loading={
                              retryRun.isPending &&
                              retryRun.variables?.correlation.retry_of ===
                                run.id
                            }
                            onClick={() => setPendingRetry(run)}
                          />
                        )}
                      {(run.state.kind === "queued" ||
                        run.state.kind === "active") && (
                        <IconButton
                          label={`Cancel ${run.ticker} run`}
                          tooltip="Cancel run — keep in history"
                          icon={<StopIcon />}
                          tone="quiet"
                          disabled={cancelRun.isPending || bulkActionPending}
                          onClick={() => setPendingCancel(run)}
                        />
                      )}
                      {manageableRunIds.has(run.id) && (
                        <TrashButton
                          label={`Delete ${run.ticker} run`}
                          disabled={bulkActionPending}
                          onClick={() => setPendingDelete(run)}
                        />
                      )}
                    </div>
                  </TableCell>
                </ClickableTableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      {editingDraftId && (
        <DraftRunEditorModal
          draftId={editingDraftId}
          onClose={() => setEditingDraftId(null)}
        />
      )}
      {pendingCancel && (
        <ConfirmDialog
          title={`Cancel ${pendingCancel.ticker} run?`}
          description="The active model process will stop and this run will remain in history as canceled."
          confirmLabel="Cancel run"
          busy={cancelRun.isPending}
          onCancel={() => setPendingCancel(null)}
          onConfirm={() => cancel(pendingCancel)}
        />
      )}
      {pendingDelete && (
        <ConfirmDialog
          title={`Delete ${pendingDelete.ticker} run?`}
          description="This removes the run and its published model from the workspace and stops active execution. Company documents and audit records are retained."
          confirmLabel="Delete run"
          busy={deleteRun.isPending}
          onCancel={() => setPendingDelete(null)}
          onConfirm={() =>
            deleteRun.mutate(pendingDelete.id, {
              onSuccess: () => {
                toast.success("Run deleted.");
                setPendingDelete(null);
              },
              onError: (error) => toast.error(error.message),
            })
          }
        />
      )}
      {pendingBulkCancel && cancellableRuns.length > 0 && (
        <ConfirmDialog
          title={`Cancel ${runCount(cancellableRuns.length)}?`}
          description="The selected non-terminal runs will be canceled and remain in run history."
          confirmLabel={
            cancellableRuns.length === 1 ? "Cancel run" : "Cancel runs"
          }
          busy={cancelRuns.isPending}
          onCancel={() => setPendingBulkCancel(false)}
          onConfirm={cancelSelectedRuns}
        />
      )}
      {pendingBulkDelete && selectedRuns.length > 0 && (
        <ConfirmDialog
          title={`Delete ${runCount(selectedRuns.length)}?`}
          description="This removes the selected runs and their published models from the workspace and stops active execution. Company documents and audit records are retained."
          confirmLabel={
            selectedRuns.length === 1 ? "Delete run" : "Delete runs"
          }
          busy={deleteRuns.isPending}
          onCancel={() => setPendingBulkDelete(false)}
          onConfirm={deleteSelectedRuns}
        />
      )}
      {pendingRetry && (
        <ConfirmDialog
          title={`Retry ${pendingRetry.ticker} run?`}
          description="This starts a new model run with the same brief, starting model, execution settings, and attached inputs, using the latest Agent version. The failed run will remain in history."
          confirmLabel="Retry run"
          onCancel={() => setPendingRetry(null)}
          onConfirm={() => retry(pendingRetry)}
        />
      )}
    </div>
  );
}
